'use client';

import { ICON_SPRITE_URL } from '@lib/icons/sprite-config';
import { useEffect } from 'react';

const HOST_ID = 'icon-sprite';

/**
 * Fetches the external icon sprite once and injects its `<symbol>`s into the
 * document so the design system's `<Icon>` (`<use href="#name">`) can resolve
 * them. This is the storefront's icon-sprite delivery: the sprite is served from
 * the cacheable /icons/sprite.svg route rather than inlined into every page.
 *
 * Renders nothing itself — the sprite is appended to `<body>` outside React's
 * root, so it never participates in hydration and cannot cause a mismatch. The
 * fetched file is HTTP-cached (see the /icons/sprite.svg route), so it is
 * fetched at most once per session and shared across every page.
 *
 * Trade-off: icons paint once the sprite arrives (a brief first-visit fill-in)
 * rather than at first paint. Icons are not the LCP element and render into
 * already-sized `<svg>` boxes, so this does not affect LCP or CLS. (The no-flash
 * alternative — native `<use href="/icons/sprite.svg#name">` — needs the DS
 * `setIconSpriteHref` API from @chemist4u/newt-components@5.0.0; a follow-up.)
 */
export default function IconSpriteLoader() {
  useEffect(() => {
    // Already injected (e.g. client-side navigation) — nothing to do.
    if (document.getElementById(HOST_ID)) {
      return;
    }

    let cancelled = false;

    fetch(ICON_SPRITE_URL)
      .then((response) => (response.ok ? response.text() : null))
      .then((markup) => {
        if (cancelled || !markup || document.getElementById(HOST_ID)) {
          return;
        }

        const host = document.createElement('div');
        host.id = HOST_ID;
        host.setAttribute('aria-hidden', 'true');
        host.style.display = 'none';
        host.innerHTML = markup;
        document.body.appendChild(host);
      })
      .catch(() => {
        // Sprite fetch failed — icons stay blank rather than throwing. The
        // route is long-cached and same-origin, so this should be rare.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
